import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { LogoutOutlined } from "@ant-design/icons";
import { SERVER_URL } from "../../utils/axios";

const assetBase = (() => {
  const base = String(SERVER_URL || "").replace(/\/$/, "");
  return base || "https://staging-api.wowbackupandrestore.com";
})();

/** Horizontal brand mark — same asset as marketing site / Auth0 login */
export const BRAND_LOGO_URL =
  "https://wowbackupandrestore.com/images/wow-logo.svg";

/**
 * Split layout matching Auth0 Universal Login (Sign In page):
 * blue hero (left) + full white form panel (right), no nested card.
 */
export default function AuthPageShell({
  title,
  subtitle,
  children,
  footer,
  heroTitle,
  showLogout = false,
}) {
  const { logout } = useAuth0();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };
  const heading =
    heroTitle ||
    (
      <>
        Welcome! <br />
        Please complete your <br />
        <span className="relative inline-block">
          WOW Backup &amp; Restore
          <img
            className="absolute -bottom-2 left-0 w-full max-w-[600px] h-5 mt-5"
            src={`${assetBase}/assets/line.png`}
            alt=""
          />
        </span>{" "}
        profile
      </>
    );

  return (
    <div className="p-3 flex flex-col lg:flex-row w-full min-h-screen bg-[#f3f8ff] font-[Inter,system-ui,sans-serif]">
      {/* Left hero — 50% */}
      <div className="bg-[#007BFF] relative overflow-hidden hidden lg:flex lg:w-1/2 lg:min-w-0 min-h-[calc(100vh-1.5rem)] rounded-xl lg:rounded-r-none flex-col items-center text-white">
        <h1 className="text-5xl relative z-50 font-bold text-center leading-[3.6rem] tracking-wide mt-24 px-8">
          {heading}
        </h1>
        <img
          src={`${assetBase}/assets/signup.png`}
          alt=""
          className="relative z-20 w-[60%] max-w-md mt-6"
        />
        <img
          src={`${assetBase}/assets/signup-bg.png`}
          alt=""
          className="absolute z-10 right-0 left-0 h-full w-full object-cover"
        />
      </div>

      {/* Right panel — full white half (matches Auth0 login), form centered inside */}
      <div className="w-full lg:w-1/2 lg:min-w-0 bg-white rounded-xl lg:rounded-l-none min-h-[calc(100vh-1.5rem)] flex flex-col items-center overflow-y-auto px-6 sm:px-10 lg:px-14 xl:px-20 py-8 sm:py-10 lg:py-14 relative">
        {showLogout ? (
          <button
            type="button"
            onClick={handleLogout}
            className="absolute top-6 right-6 sm:top-8 sm:right-8 lg:top-10 lg:right-10 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-red-600 transition-colors"
            aria-label="Log out"
          >
            <LogoutOutlined className="text-base" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        ) : null}
        <div className="w-full max-w-[480px] flex flex-col flex-1 justify-center my-auto">
          <div className="flex justify-center mb-8">
            <img
              src={BRAND_LOGO_URL}
              alt="WOW Backup and Restore"
              className="h-11 sm:h-12 w-auto max-w-[min(100%,340px)] object-contain"
              width={340}
              height={48}
            />
          </div>

          <h1 className="text-center font-semibold text-xl sm:text-2xl text-gray-900 mb-2">
            {title}
          </h1>
          {subtitle ? (
            <p className="text-center text-sm text-gray-500 leading-relaxed mb-6">
              {subtitle}
            </p>
          ) : (
            <div className="mb-6" />
          )}

          {children}
        </div>

        {footer !== false ? (
          <div className="w-full max-w-[480px] mt-8 pb-2 text-sm text-center shrink-0">
            <p className="mb-1 text-gray-400">If you need further assistance</p>
            <a
              href="https://wowbackupandrestore.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#007BFF] hover:underline"
            >
              Contact our support team
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
